import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Zap } from 'lucide-react';
interface Notification {
  name: string;
  country: string;
  flag: string;
  action: 'bought' | 'cart';
}
const notifications: Notification[] = [{
  name: 'María G.',
  country: 'Colombia',
  flag: '🇨🇴',
  action: 'bought'
}, {
  name: 'Carlos R.',
  country: 'Argentina',
  flag: '🇦🇷',
  action: 'cart'
}, {
  name: 'Ana L.',
  country: 'Venezuela',
  flag: '🇻🇪',
  action: 'bought'
}, {
  name: 'Carmen S.',
  country: 'Perú',
  flag: '🇵🇪',
  action: 'bought'
}, {
  name: 'Luis H.',
  country: 'Chile',
  flag: '🇨🇱',
  action: 'cart'
}, {
  name: 'Patricia V.',
  country: 'Ecuador',
  flag: '🇪🇨',
  action: 'bought'
}, {
  name: 'Roberto M.',
  country: 'Bolivia',
  flag: '🇧🇴',
  action: 'bought'
}, {
  name: 'Jorge A.',
  country: 'Guatemala',
  flag: '🇬🇹',
  action: 'cart'
}, {
  name: 'Isabel F.',
  country: 'Costa Rica',
  flag: '🇨🇷',
  action: 'bought'
}, {
  name: 'Miguel T.',
  country: 'Uruguay',
  flag: '🇺🇾',
  action: 'bought'
}, {
  name: 'Sofía N.',
  country: 'Paraguay',
  flag: '🇵🇾',
  action: 'cart'
}, {
  name: 'Diego P.',
  country: 'Honduras',
  flag: '🇭🇳',
  action: 'bought'
}, {
  name: 'Lucía M.',
  country: 'El Salvador',
  flag: '🇸🇻',
  action: 'bought'
}, {
  name: 'Andrés V.',
  country: 'Nicaragua',
  flag: '🇳🇮',
  action: 'cart'
}, {
  name: 'Rosa T.',
  country: 'Panamá',
  flag: '🇵🇦',
  action: 'bought'
}];
const DISPLAY_DURATION = 5000;
const PAUSE_BETWEEN = 15000;
export function SocialProofPopup() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [progress, setProgress] = useState(100);
  const showNext = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % notifications.length);
    setProgress(100);
    setVisible(true);
  }, []);
  useEffect(() => {
    if (dismissed) return;
    const initialDelay = setTimeout(() => setVisible(true), 4000);
    return () => clearTimeout(initialDelay);
  }, [dismissed]);
  useEffect(() => {
    if (!visible || dismissed) return;
    const hideTimer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => {
        if (!dismissed) showNext();
      }, PAUSE_BETWEEN);
    }, DISPLAY_DURATION);
    return () => clearTimeout(hideTimer);
  }, [visible, dismissed, showNext]);
  useEffect(() => {
    if (!visible || dismissed) return;
    setProgress(100);
    const interval = setInterval(() => {
      setProgress(prev => {
        const next = prev - 100 / (DISPLAY_DURATION / 100);
        return next <= 0 ? 0 : next;
      });
    }, 100);
    return () => clearInterval(interval);
  }, [visible, dismissed]);
  if (dismissed) return null;
  const notif = notifications[currentIndex];
  const isBought = notif.action === 'bought';
  return <AnimatePresence data-id="element-578">
      {visible && <motion.div key={currentIndex} initial={{
      opacity: 0,
      x: -60,
      y: 0
    }} animate={{
      opacity: 1,
      x: 0,
      y: 0
    }} exit={{
      opacity: 0,
      x: -60,
      y: 0
    }} transition={{
      type: 'spring',
      stiffness: 320,
      damping: 28
    }} className="fixed bottom-16 left-3 z-[1500] max-w-[260px]" data-id="element-579">
          <div className="bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.10)] border border-[#EEEEEE] overflow-hidden" data-id="element-580">
            {/* Progress bar */}
            <div className="h-[3px] bg-[#F0F4EC] w-full" data-id="element-581">
              <motion.div className={`h-full ${isBought ? 'bg-[#2D5A3D]' : 'bg-[#C17817]'}`} initial={{
            width: '100%'
          }} animate={{
            width: `${progress}%`
          }} transition={{
            duration: 0.1,
            ease: 'linear'
          }} data-id="element-582" />
            </div>

            <div className="px-3 py-2.5 flex items-center gap-2.5" data-id="element-583">
              {/* Icon */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isBought ? 'bg-[#E8F5EE]' : 'bg-[#FEF3E2]'}`} data-id="element-584">
                {isBought ? <Zap className="w-4 h-4 text-[#2D5A3D]" data-id="element-585" /> : <ShoppingCart className="w-4 h-4 text-[#C17817]" data-id="element-586" />}
              </div>

              {/* Content — name + country */}
              <div className="flex-1 min-w-0" data-id="element-587">
                <p className="text-xs font-bold text-[#2C1810] mb-0.5" data-id="element-588">
                  {notif.name}
                </p>
                <p className="text-xs text-[#2C1810] leading-snug" data-id="element-589">
                  {isBought ? <span className="text-[#2D5A3D] font-semibold" data-id="element-590">
                      acaba de comprar{' '}
                      <span className="italic" data-id="element-591">"La Medicina Natural"</span>
                    </span> : <span className="text-[#C17817] font-semibold" data-id="element-592">
                      agregó al carrito{' '}
                      <span className="italic" data-id="element-593">"La Medicina Natural"</span>
                    </span>}
                </p>
              </div>

              {/* Close */}
              <button onClick={() => setDismissed(true)} className="w-5 h-5 flex items-center justify-center rounded-full text-[#8B7355] hover:bg-[#F0F4EC] transition-colors shrink-0" data-id="element-594">
                <X className="w-3 h-3" data-id="element-595" />
              </button>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}